'use client'

import { useEffect, useMemo } from 'react'
import { useSearchParams } from 'next/navigation'

export const REGISTER_INTEREST_SOURCE_KEY = 'register_interest_source'

/** Returns all stored sources. Handles both legacy plain-string and new JSON-array formats. */
export const getStoredSources = (): string[] => {
  if (typeof window === 'undefined') return []
  const raw = window.localStorage.getItem(REGISTER_INTEREST_SOURCE_KEY)
  if (!raw) return []
  try {
    const parsed = JSON.parse(raw)
    if (Array.isArray(parsed))
      return parsed.filter((s): s is string => typeof s === 'string')
    if (typeof parsed === 'string') return [parsed]
  } catch {
    return [raw]
  }
  return []
}

const getCurrentSearchParam = (...keys: string[]) => {
  if (typeof window === 'undefined') return null
  const params = new URLSearchParams(window.location.search)
  return keys.map((key) => params.get(key)).find(Boolean) ?? null
}

export const getInterestSourceFromUrl = (searchParams?: URLSearchParams | null) => {
  if (searchParams) {
    return searchParams.get('source') || searchParams.get('utm_source')
  }
  return getCurrentSearchParam('source', 'utm_source')
}

export const getInterestSource = (searchParams?: URLSearchParams | null) =>
  getInterestSourceFromUrl(searchParams) || getStoredSources()[0] || null

/** Adds the URL source to the stored array without overwriting existing sources. */
export const persistInterestSourceFromUrl = (searchParams?: URLSearchParams | null) => {
  const sourceFromUrl = getInterestSourceFromUrl(searchParams)
  if (!sourceFromUrl) return

  const existing = getStoredSources()
  const normalizedNew = normalizeSourceKey(sourceFromUrl)
  const alreadyStored = existing.some((s) => normalizeSourceKey(s) === normalizedNew)
  if (!alreadyStored) {
    localStorage.setItem(
      REGISTER_INTEREST_SOURCE_KEY,
      JSON.stringify([...existing, sourceFromUrl]),
    )
  }
}

export const normalizeSourceKey = (value: string) =>
  value.toLowerCase().replace(/[^a-z0-9]/g, '')

export const useRegisterInterestSource = () => {
  const searchParams = useSearchParams()

  const source = useMemo(() => getInterestSource(searchParams), [searchParams])

  useEffect(() => {
    persistInterestSourceFromUrl(searchParams)
  }, [searchParams])

  return source
}
